import { useDataTableContext } from '../../components/DataTable/DataTableContext'
import useApiService from '../../services/api'
import lodash from 'lodash'
import { useMemo, useState } from 'react'
import confirm from '../../components/dialogConfirm'
import { CompanyTypeSlug, TableTypeSlug } from '../../models/companies'
import { Combobox } from '../../components/UI/combobox/combobox'
import { CreateEditCampaignModal } from './CreateEditCampaignModal'
import { CreateCampaignModalV2 } from './CreateCampaignModalV2'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export interface AssignToCampaignModalProps {
  accessorKey: string
  tableQueryKey: string[]
  requestParams: Record<string, any>
  tableTypeSlug: TableTypeSlug
  companyTypeSlug?: CompanyTypeSlug
  data: any[]
  buttonLess?: boolean
}

export function AssignToCampaignModal(props: AssignToCampaignModalProps) {
  const api = useApiService()
  const featureFlags = useFeatureFlagService()
  const isMultiType = !!featureFlags.enableEmailSequencing
  const { data: campaigns, isLoading } = api.useGetCampaigns()

  const mutation = api.useAssignToCampaignMutation(
    props.tableQueryKey,
    props.accessorKey,
    props.tableTypeSlug
  )
  const {
    state: { rowSelection, isAllRowsSelected, totalSelectedRows },
  } = useDataTableContext()

  const [openNewCampaign, setOpenNewCampaign] = useState(false)

  const campaignOptions = useMemo(
    () =>
      campaigns?.results.map((c) => ({
        label: c.name,
        value: c.id,
        icon: (
          <div
            className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
            style={{ backgroundColor: c.color }}
          />
        ),
      })) ?? [],
    [campaigns]
  )

  const selectedRows = props.data?.filter((_, i) => {
    return Boolean(rowSelection?.[i])
  })

  const unselectedRows = props.data?.filter((_, i) => {
    return !rowSelection?.[i]
  })

  const selectedChainProxyIds = selectedRows?.map((r: any) =>
    lodash.get(r, props.accessorKey)
  ) as number[]

  const unselectedChainProxyIds = unselectedRows?.map((r: any) =>
    lodash.get(r, props.accessorKey)
  ) as number[]

  async function handleSubmit(
    campaignId: number | string,
    campaignName?: string,
    campaignColor?: string,
    campaignTypeSlug?: string
  ) {
    const selected = campaigns?.results.find((it) => it.id == campaignId)
    const targetTypeSlug = props.companyTypeSlug ?? ''
    // Use explicitly passed type (for newly created campaigns not yet in cache)
    // or fall back to cached campaign data
    const existingTypeSlug = campaignTypeSlug ?? selected?.type?.slug

    // Detect type mismatch: pushing type X targets to a type Y campaign
    const hasMismatch =
      !!existingTypeSlug &&
      !!targetTypeSlug &&
      existingTypeSlug !== targetTypeSlug

    if (hasMismatch) {
      const typeName = selected?.type?.name ?? existingTypeSlug
      const confirmed = await confirm(
        `Adding these targets to a ${typeName} campaign will clear the opportunity type, which limits the columns visible in your campaign targets table. Continue?`,
        'Opportunity Type Mismatch'
      )
      if (!confirmed) return
    } else {
      const confirmed = await confirm(
        `Confirm assigning ${totalSelectedRows} items to campaign?`,
        'Assign to Campaign'
      )
      if (!confirmed) return
    }

    await mutation.mutateAsync({
      campaign_name: campaignName ?? selected?.name ?? '',
      campaign_color: campaignColor ?? selected?.color ?? '',
      campaign_id: parseInt(campaignId.toString()),
      include_ids: isAllRowsSelected ? [] : selectedChainProxyIds,
      exclude_ids: unselectedChainProxyIds,
      filter_params: props.requestParams,
      target_type_slug: targetTypeSlug,
      clear_type: hasMismatch,
    })
  }

  return (
    <>
      {!openNewCampaign && (
        <Combobox
          options={campaignOptions}
          searchPlaceholder="Search Campaigns..."
          placeholder="Push to Campaign"
          onSelectAltAction={handleSubmit}
          onAddNew={() => setOpenNewCampaign(true)}
          loading={isLoading || mutation.isPending}
          buttonLess={props.buttonLess}
        />
      )}
      {isMultiType || !props.companyTypeSlug ? (
        <CreateCampaignModalV2
          key={props.companyTypeSlug ?? 'none'}
          show={openNewCampaign}
          onHide={() => setOpenNewCampaign(false)}
          defaultTypeSlug={props.companyTypeSlug}
          onCreated={async (campaign) => {
            await handleSubmit(
              campaign.id,
              campaign.name,
              campaign.color,
              campaign.typeSlug
            )
          }}
        />
      ) : (
        <CreateEditCampaignModal
          isOpen={openNewCampaign}
          onClose={() => setOpenNewCampaign(false)}
          defaultCampaignCompanyType={props.companyTypeSlug}
          pushToCampaignOptions={{
            data: props.data,
            accessorKey: props.accessorKey,
            requestParams: props.requestParams,
            tableQueryKey: props.tableQueryKey,
            totalSelectedRows: totalSelectedRows,
          }}
        />
      )}
    </>
  )
}
